<template>
  <div class="docs-app">
    <header class="app-header">
      <div class="brand-group">
        <button class="menu-toggle" type="button" aria-label="Open navigation" @click="mobileNavigationOpen = true">
          <span></span><span></span><span></span>
        </button>
        <img class="brand-logo" :src="isDark ? '/iosea-logo-white-cropped.png' : '/iosea-logo-cropped.png'" alt="IOSEA">
        <span class="brand-label">API Docs</span>
      </div>

      <div class="header-controls">
        <!--
        <label class="version-control">
          <span>Version</span>
          <select v-model="selectedAPIVersion" aria-label="API version">
            <option v-for="version in apiVersions" :key="version.value" :value="version.value">
              {{ version.label }}
            </option>
          </select>
        </label>
        -->

        <button
          class="theme-switch"
          :class="{ active: isDark }"
          type="button"
          role="switch"
          :aria-checked="isDark"
          :aria-label="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
          @click="toggleTheme"
        >
          <span class="theme-switch-thumb">
            <svg v-if="isDark" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.5 15.7A8.8 8.8 0 0 1 8.3 3.5 8.8 8.8 0 1 0 20.5 15.7Z"></path>
            </svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="4"></circle>
              <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1"></path>
            </svg>
          </span>
        </button>

        <details v-if="user" ref="accountMenu" class="account-menu">
          <summary aria-label="Open account menu">
            <span class="account-avatar">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="4"></circle>
                <path d="M4.5 21c.7-4.2 3.3-6.3 7.5-6.3s6.8 2.1 7.5 6.3"></path>
              </svg>
            </span>
          </summary>
          <div class="account-dropdown">
            <div class="account-email">{{ user.email }}</div>
            <button type="button" @click="onClickLogout">Logout</button>
          </div>
        </details>
      </div>
    </header>

    <div class="docs-shell">
      <aside class="sidebar desktop-navigation">
        <DocsNavigation
          v-model:search="navigationSearch"
          :groups="filteredNavigationGroups"
          search-id="desktop-docs-search"
          :selected-key="sectionNumber"
          @select="selectPage"
        />
      </aside>

      <div v-if="mobileNavigationOpen" class="navigation-backdrop" @click="mobileNavigationOpen = false"></div>
      <aside class="sidebar mobile-navigation" :class="{ open: mobileNavigationOpen }">
        <div class="mobile-navigation-heading">
          <strong>Documentation</strong>
          <button type="button" aria-label="Close navigation" @click="mobileNavigationOpen = false">Close</button>
        </div>
        <DocsNavigation
          v-model:search="navigationSearch"
          :groups="filteredNavigationGroups"
          search-id="mobile-docs-search"
          :selected-key="sectionNumber"
          @select="selectPage"
        />
      </aside>

      <main ref="mainContent" class="docs-content" tabindex="-1">
        <div class="page-toolbar">
          <p class="breadcrumb">{{ breadcrumb }}</p>
          <button class="share-button" type="button" @click="copyPageLink">
            {{ pageLinkCopied ? 'Link copied' : 'Copy page link' }}
          </button>
        </div>

        <HTTPRequest
          v-if="contentKind === 'http'"
          :section-number="sectionNumber"
          :title="selectedPage.title"
          :description="selectedPage.description"
          :request="selectedPage.request"
          :endpoint="selectedPage.endpoint"
          :parameters="selectedPage.parameters"
          :body="selectedPage.body"
          :returns="selectedPage.returns"
          :introduced-in="introducedIn"
        />
        <Websocket
          v-else-if="contentKind === 'websocket'"
          :title="selectedPage.title"
          :short-description="selectedPage.shortDescription"
          :description="selectedPage.description"
          :endpoint="selectedPage.endpoint"
          :returns="selectedPage.returns"
          :on-open-desc="selectedPage.onOpenDesc"
          :on-close-desc="selectedPage.onCloseDesc"
          :introduced-in="introducedIn"
        />
        <File
          v-else
          :title="selectedPage.title"
          :content="selectedPage.content"
          :file-name="selectedPage.fileName"
        />
      </main>
    </div>

    <footer class="app-footer">
      <div>Copyright &copy; 2026 IOSEA Inc. All rights reserved.</div>
      <div class="footer-version">Documentation version {{ documentationVersion }}</div>
    </footer>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DocsNavigation from '../components/DocsNavigation.vue';
import File from '../components/File.vue';
import HTTPRequest from '../components/HTTPRequest.vue';
import Websocket from '../components/Websocket.vue';
import getUser from '../composables/getUser';
import useLogout from '../composables/useLogout';
import { DOCUMENTATION_VERSION } from '../config/app';
import jsonv1 from '../json/details-v1.json';
import jsonv2 from '../json/details-v2.json';
import jsonv3 from '../json/details-v3.json';

const documents = {
  '1x': jsonv1,
  '2x': jsonv2,
  '3x': jsonv3
};

const groupLabels = {
  overview: 'Getting started',
  http: 'HTTP Requests',
  websocket: 'WebSockets',
  health: 'Health Check',
  deployment: 'Deployment'
};

const categoryLabels = {
  overview: '',
  '4-1': 'Network',
  '4-2': 'Locator',
  '4-3': 'Tag',
  '4-4': 'Account',
  '4-5': 'Debugger',
  '4-6': 'Diagnosis',
  '4-7': 'Firmware',
  '4-8': 'Co-pilot',
  '4-9': 'Siteplanner',
  '5-1': 'Connection setup',
  '5-channels': 'Channels',
  '5-6': 'Gateway client connection',
  '5-9': 'Gateway client connection',
  '6-1': 'Concurrent connections',
  '6-2': 'Microservices',
  '7-1': 'Protobuf',
  '7-2': 'VM'
};

const v3IntroducedIn = {
  '4-2-2': '3.4.4',
  '4-2-4': '3.4.4',
  '4-2-19': '3.5.0',
  '4-4-1': '3.5.0',
  '4-4-3': '3.5.0',
  '4-5-1': '3.1.6',
  '4-5-2': '3.1.6',
  '4-5-3': '3.1.6',
  '4-5-4': '3.1.6',
  '4-5-5': '3.1.6',
  '4-5-6': '3.1.6',
  '4-6-1': '3.2.0',
  '4-6-2': '3.2.0',
  '4-6-3': '3.2.0',
  '4-6-4': '3.2.3',
  '4-7-1': '3.3.0',
  '4-7-2': '3.3.0',
  '4-7-3': '3.3.0',
  '4-7-4': '3.3.0',
  '4-8-1': '3.4.0',
  '4-8-2': '3.4.0',
  '4-8-3': '3.4.0',
  '4-8-4': '3.4.0',
  '4-8-5': '3.4.0',
  '4-8-6': '3.4.0',
  '4-8-7': '3.4.0',
  '4-9-1': '3.4.3',
  '4-9-2': '3.4.3',
  '4-9-3': '3.4.3',
  '4-9-4': '3.4.3',
  '5-10': '3.1.6',
  '5-11': '3.1.6',
  '5-12': '3.1.6',
  '5-13': '3.4.0',
  '5-14': '3.4.0',
  '5-15': '3.4.3'
};

const groupForKey = (key) => {
  if (!key.includes('-')) return 'overview';
  if (key.startsWith('4-')) return 'http';
  if (key.startsWith('5-')) return 'websocket';
  if (key.startsWith('6-')) return 'health';
  return 'deployment';
};

const categoryForKey = (key, group) => {
  if (group === 'overview') return 'overview';
  const segments = key.split('-');
  if (group === 'websocket' && segments.length === 2) return '5-channels';
  return segments.slice(0, 2).join('-');
};

const hiddenNavigationKeys = new Set(['3']);

export default {
  name: 'Home',
  components: { DocsNavigation, File, HTTPRequest, Websocket },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { user } = getUser();
    const { logout, error } = useLogout();
    const displayDockerCompose = window.localStorage.getItem('displayDockerCompose') === 'true';
    const initialVersion = documents[route.query.version] ? route.query.version : '3x';
    const selectedAPIVersion = ref(initialVersion);
    const activeDocuments = computed(() => documents[selectedAPIVersion.value]);
    const querySection = typeof route.query.section === 'string' ? route.query.section : '1';
    const sectionNumber = ref(activeDocuments.value[querySection] ? querySection : '1');
    const navigationSearch = ref('');
    const mobileNavigationOpen = ref(false);
    const pageLinkCopied = ref(false);
    const isDark = ref(false);
    const mainContent = ref(null);
    const accountMenu = ref(null);
    const documentationVersion = DOCUMENTATION_VERSION;
    let copiedResetTimeout;

    const apiVersions = [
      { value: '1x', label: '1.x' },
      { value: '2x', label: '2.x' },
      { value: '3x', label: '3.x' }
    ];

    const navigationGroups = computed(() => {
      const categoryMap = new Map();

      Object.entries(activeDocuments.value)
        .filter(([key]) => !hiddenNavigationKeys.has(key))
        .filter(([key]) => displayDockerCompose || key !== '7-2-1')
        .sort(([first], [second]) => first.localeCompare(second, undefined, { numeric: true }))
        .forEach(([key, page]) => {
          const group = groupForKey(key);
          const category = categoryForKey(key, group);
          if (!categoryMap.has(group)) categoryMap.set(group, new Map());
          if (!categoryMap.get(group).has(category)) categoryMap.get(group).set(category, []);
          categoryMap.get(group).get(category).push({
            key,
            title: page.title,
            endpoint: page.endpoint,
            method: page.request || (group === 'websocket' ? 'WS' : '')
          });
        });

      return ['overview', 'http', 'websocket', 'health', 'deployment']
        .filter((group) => categoryMap.has(group))
        .map((group) => ({
          id: group,
          label: groupLabels[group],
          categories: Array.from(categoryMap.get(group), ([id, entries]) => ({
            id,
            label: categoryLabels[id],
            entries
          }))
        }));
    });

    const filteredNavigationGroups = computed(() => {
      const search = navigationSearch.value.trim().toLowerCase();
      if (!search) return navigationGroups.value;

      return navigationGroups.value.map((group) => ({
        ...group,
        categories: group.categories.map((category) => ({
          ...category,
          entries: category.entries.filter((entry) => (
            entry.title.toLowerCase().includes(search)
              || (entry.endpoint || '').toLowerCase().includes(search)
              || (entry.method || '').toLowerCase().includes(search)
          ))
        })).filter((category) => category.entries.length)
      })).filter((group) => group.categories.length);
    });

    const selectedPage = computed(() => activeDocuments.value[sectionNumber.value] || activeDocuments.value['1']);
    const introducedIn = computed(() => (
      selectedAPIVersion.value === '3x' ? v3IntroducedIn[sectionNumber.value] || '' : ''
    ));
    const contentKind = computed(() => {
      if (sectionNumber.value.startsWith('5-') && !sectionNumber.value.startsWith('5-1-')) return 'websocket';
      if (sectionNumber.value.startsWith('7-')) return 'file';
      return 'http';
    });
    const breadcrumb = computed(() => {
      const group = groupForKey(sectionNumber.value);
      const category = categoryLabels[categoryForKey(sectionNumber.value, group)];
      return [groupLabels[group], category, selectedPage.value.title].filter(Boolean).join(' / ');
    });

    const syncRoute = () => {
      if (route.query.version === selectedAPIVersion.value && route.query.section === sectionNumber.value) return;
      router.replace({
        query: {
          ...route.query,
          version: selectedAPIVersion.value,
          section: sectionNumber.value
        }
      });
    };

    const selectPage = (key) => {
      sectionNumber.value = key;
      mobileNavigationOpen.value = false;
      mainContent.value?.focus();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const copyPageLink = async () => {
      if (!navigator.clipboard) return;
      await navigator.clipboard.writeText(window.location.href);
      pageLinkCopied.value = true;
      clearTimeout(copiedResetTimeout);
      copiedResetTimeout = setTimeout(() => {
        pageLinkCopied.value = false;
      }, 1400);
    };

    const onClickLogout = async () => {
      await logout();
      if (!error.value) router.push({ name: 'SignIn' });
    };

    const closeAccountMenuOnOutsideClick = (event) => {
      if (!accountMenu.value || accountMenu.value.contains(event.target)) return;
      accountMenu.value.removeAttribute('open');
    };

    onMounted(() => {
      isDark.value = window.localStorage.getItem('theme') === 'dark';
      document.documentElement.classList.toggle('dark', isDark.value);
      document.addEventListener('click', closeAccountMenuOnOutsideClick);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', closeAccountMenuOnOutsideClick);
    });

    const toggleTheme = () => {
      isDark.value = !isDark.value;
      document.documentElement.classList.toggle('dark', isDark.value);
      window.localStorage.setItem('theme', isDark.value ? 'dark' : 'light');
    };

    watch(selectedAPIVersion, () => {
      if (!activeDocuments.value[sectionNumber.value]) sectionNumber.value = '1';
      navigationSearch.value = '';
      syncRoute();
    });
    watch(sectionNumber, syncRoute);
    watch(() => route.query, (query) => {
      if (documents[query.version] && query.version !== selectedAPIVersion.value) {
        selectedAPIVersion.value = query.version;
      }
      if (typeof query.section === 'string' && activeDocuments.value[query.section] && query.section !== sectionNumber.value) {
        sectionNumber.value = query.section;
      }
    });
    watch(user, () => {
      if (!user.value) router.push({ name: 'SignIn' });
    });

    syncRoute();

    return {
      apiVersions,
      accountMenu,
      breadcrumb,
      contentKind,
      copyPageLink,
      documentationVersion,
      filteredNavigationGroups,
      introducedIn,
      isDark,
      mainContent,
      mobileNavigationOpen,
      navigationSearch,
      onClickLogout,
      pageLinkCopied,
      sectionNumber,
      selectPage,
      selectedAPIVersion,
      selectedPage,
      toggleTheme,
      user
    };
  }
};
</script>
